import { useEffect, useState } from "react";
import { ChevronDown, Plus, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import CircularProgress from "@/components/common/CircularProgress";
import {
  comunidadAdapter,
  ComunidadCalendarEvent,
  ComunidadCalendarEventInput,
  ComunidadCalendarEventType,
  FormacionDocente,
} from "@/hooks/api/comunidad";
import { toastUtils } from "@/hooks/useToast";
import { isoToZoneParts, localTzToUTC } from "@/components/psychologist/calendar/timeZoneHelpers";

/** Los eventos se cargan en hora de Argentina; cada psicóloga los ve en su propia zona. */
const ART = "America/Argentina/Buenos_Aires";

interface EventForm {
  type: ComunidadCalendarEventType;
  title: string;
  description: string;
  date: string;
  time: string;
  /** Solo cafecitos: todavía sin fecha ("Próximamente"). */
  noDate: boolean;
  durationMinutes: string;
  meetUrl: string;
  edition: string;
  /** "" = formación nueva; si no, la key de la formación a la que se le suma esta edición. */
  formacionKey: string;
  /** Solo formaciones: quién la dicta (vale para todas sus ediciones; se guarda aparte). */
  docentes: FormacionDocente[];
}

const EMPTY_FORM: EventForm = {
  type: "formacion",
  title: "",
  description: "",
  date: "",
  time: "",
  noDate: false,
  durationMinutes: "120",
  meetUrl: "",
  edition: "1",
  formacionKey: "",
  docentes: [],
};

function toForm(ev: ComunidadCalendarEvent, docentes: FormacionDocente[]): EventForm {
  const parts = ev.startsAt ? isoToZoneParts(ev.startsAt, ART) : null;
  return {
    type: ev.type,
    title: ev.title,
    description: ev.description,
    date: parts?.date ?? "",
    time: parts?.time ?? "",
    noDate: !ev.startsAt,
    durationMinutes: String(ev.durationMinutes),
    meetUrl: ev.meetUrl ?? "",
    edition: String(ev.edition ?? 1),
    formacionKey: ev.formacionKey ?? "",
    docentes,
  };
}

/** Valida el formulario y lo pasa al formato del backend; devuelve un mensaje si falta algo. */
function toInput(f: EventForm): ComunidadCalendarEventInput | string {
  if (!f.title.trim()) return "Poné un título";
  const needsDate = f.type === "formacion" || !f.noDate;
  if (needsDate && (!f.date || !f.time)) return "Completá fecha y hora (hora de Argentina)";
  const duration = Number(f.durationMinutes);
  if (!Number.isInteger(duration) || duration < 15 || duration > 480) return "La duración va de 15 a 480 minutos";
  const meet = f.meetUrl.trim();
  if (meet && !/^https?:\/\//.test(meet)) return "El link del Meet tiene que empezar con https://";
  return {
    type: f.type,
    title: f.title.trim(),
    description: f.description.trim(),
    startsAt: needsDate ? localTzToUTC(f.date, f.time, ART).toISOString() : null,
    durationMinutes: duration,
    meetUrl: meet || null,
    edition: f.type === "formacion" ? Number(f.edition) : null,
    formacionKey: f.type === "formacion" && f.formacionKey ? f.formacionKey : null,
  };
}

function describeWhen(ev: ComunidadCalendarEvent): string {
  if (!ev.startsAt) return "Fecha a confirmar";
  const { date, time } = isoToZoneParts(ev.startsAt, ART);
  const [y, m, d] = date.split("-");
  return `${d}/${m}/${y} · ${time} hs (Argentina) · ${ev.durationMinutes} min`;
}

interface EventFieldsProps {
  form: EventForm;
  onChange: (f: EventForm) => void;
  /** Formaciones ya cargadas, para sumarles una edición. */
  formaciones: { key: string; title: string }[];
  /** Quién dicta cada formación ya cargada (por key). */
  docentesByKey: Record<string, FormacionDocente[]>;
  idPrefix: string;
}

const inputCls = "rounded-lg border border-[#C9BCE8] px-2 py-1 text-sm";

const fullName = (d: FormacionDocente) => `${d.name} ${d.surname}`.trim() || d.email;

/**
 * "¿Quién la dicta?": buscador de psicólogas (nombre, apellido o mail) + las elegidas. Quien
 * figure acá ve el espacio "Mis formaciones dictadas" una vez que la formación se dictó.
 */
const DocentesPicker = ({
  value,
  onChange,
  idPrefix,
}: {
  value: FormacionDocente[];
  onChange: (d: FormacionDocente[]) => void;
  idPrefix: string;
}) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<FormacionDocente[]>([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const found = await comunidadAdapter.searchPsicologas(q);
        if (!cancelled) setResults(found);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query]);

  const chosen = new Set(value.map((d) => d.psychologistId));
  return (
    <div className="flex flex-col gap-1.5 rounded-lg bg-[#FAF8FF] p-2 text-xs text-[#4A3D73]">
      <label htmlFor={`${idPrefix}-docente`} className="font-semibold">
        ¿Quién la dicta?
      </label>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((d) => (
            <span
              key={d.psychologistId}
              className="flex items-center gap-1 rounded-full bg-[#EDE6FA] py-0.5 pl-2.5 pr-1 font-semibold text-[#665090]"
            >
              {fullName(d)}
              <button
                type="button"
                aria-label={`Sacar a ${fullName(d)}`}
                className="rounded-full p-0.5 hover:bg-[#DCD0F5]"
                onClick={() => onChange(value.filter((x) => x.psychologistId !== d.psychologistId))}
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}
      <input
        id={`${idPrefix}-docente`}
        className={inputCls}
        placeholder="Buscá a la psicóloga por nombre o mail"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        autoComplete="off"
      />
      {searching && <span className="text-gray-400">Buscando…</span>}
      {!searching && query.trim().length >= 2 && results.length === 0 && (
        <span className="text-gray-400">No encontramos a nadie con ese nombre o mail.</span>
      )}
      {results.length > 0 && (
        <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
          {results.map((r) => (
            <li key={r.psychologistId}>
              <button
                type="button"
                disabled={chosen.has(r.psychologistId)}
                onClick={() => {
                  onChange([...value, r]);
                  setQuery("");
                  setResults([]);
                }}
                className="w-full rounded-md px-2 py-1 text-left hover:bg-[#EDE6FA] disabled:cursor-default disabled:opacity-50"
              >
                <span className="font-semibold text-[#2D2049]">{fullName(r)}</span>
                <span className="text-gray-500"> · {r.email}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <span className="text-[11px] text-gray-400">
        Vale para todas las ediciones. Quien la dicta ve «Mis formaciones dictadas» con su certificado.
      </span>
    </div>
  );
};

const EventFields = ({ form, onChange, formaciones, docentesByKey, idPrefix }: EventFieldsProps) => {
  const set = (patch: Partial<EventForm>) => onChange({ ...form, ...patch });
  const isFormacion = form.type === "formacion";
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-2">
        {(["formacion", "cafecito"] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => set({ type: t, ...(t === "formacion" ? { noDate: false } : { formacionKey: "" }) })}
            className={`rounded-full border px-3 py-1 text-xs font-semibold ${
              form.type === t ? "border-[#665090] bg-[#665090] text-white" : "border-[#C9BCE8] bg-white text-[#665090]"
            }`}
          >
            {t === "formacion" ? "Formación" : "Cafecito"}
          </button>
        ))}
      </div>

      {isFormacion && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-[#4A3D73]">
          <label htmlFor={`${idPrefix}-edition`} className="font-semibold">Edición</label>
          <select
            id={`${idPrefix}-edition`}
            className={inputCls}
            value={form.edition}
            onChange={(e) => set({ edition: e.target.value })}
          >
            {[1, 2, 3].map((n) => (
              <option key={n} value={n}>{n}ª</option>
            ))}
          </select>
          {Number(form.edition) > 1 && (
            <>
              <label htmlFor={`${idPrefix}-formacion`} className="font-semibold">de</label>
              <select
                id={`${idPrefix}-formacion`}
                className={`${inputCls} min-w-0 max-w-full`}
                value={form.formacionKey}
                onChange={(e) => {
                  const f = formaciones.find((x) => x.key === e.target.value);
                  set({
                    formacionKey: e.target.value,
                    docentes: docentesByKey[e.target.value] ?? [],
                    ...(f ? { title: f.title } : {}),
                  });
                }}
              >
                <option value="">Formación nueva</option>
                {formaciones.map((f) => (
                  <option key={f.key} value={f.key}>{f.title}</option>
                ))}
              </select>
            </>
          )}
        </div>
      )}

      <input
        id={`${idPrefix}-title`}
        className={`${inputCls} font-semibold`}
        placeholder={isFormacion ? "Título de la formación" : "Título (ej. Espacio Cafecito)"}
        value={form.title}
        onChange={(e) => set({ title: e.target.value })}
      />
      <textarea
        id={`${idPrefix}-description`}
        className={`${inputCls} min-h-[70px]`}
        placeholder="Descripción (va en el evento de Google Calendar que agenda cada psicóloga)"
        value={form.description}
        onChange={(e) => set({ description: e.target.value })}
      />

      {!isFormacion && (
        <label className="flex items-center gap-2 text-xs text-[#4A3D73]">
          <input
            id={`${idPrefix}-nodate`}
            type="checkbox"
            className="accent-[#665090]"
            checked={form.noDate}
            onChange={(e) => set({ noDate: e.target.checked })}
          />
          Todavía sin fecha (se muestra como «Próximamente»)
        </label>
      )}

      {!(form.noDate && !isFormacion) && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-[#4A3D73]">
          <input
            id={`${idPrefix}-date`}
            type="date"
            className={inputCls}
            value={form.date}
            onChange={(e) => set({ date: e.target.value })}
          />
          <input
            id={`${idPrefix}-time`}
            type="time"
            className={inputCls}
            value={form.time}
            onChange={(e) => set({ time: e.target.value })}
          />
          <span>hora de Argentina ·</span>
          <input
            id={`${idPrefix}-duration`}
            type="number"
            min={15}
            max={480}
            step={15}
            className={`${inputCls} w-20`}
            value={form.durationMinutes}
            onChange={(e) => set({ durationMinutes: e.target.value })}
          />
          <span>minutos</span>
        </div>
      )}

      <input
        id={`${idPrefix}-meet`}
        className={`${inputCls} text-xs`}
        placeholder="Link del Meet (https://meet.google.com/…)"
        value={form.meetUrl}
        onChange={(e) => set({ meetUrl: e.target.value })}
      />
      {isFormacion && (
        <DocentesPicker value={form.docentes} onChange={(docentes) => set({ docentes })} idPrefix={idPrefix} />
      )}
      {isFormacion && (
        <p className="m-0 text-[11px] text-gray-400">
          La duración define el 75% de asistencia para «Calificá», que se abre al terminar la 1ª edición y
          se cierra a fin de ese mes.
        </p>
      )}
    </div>
  );
};

/**
 * Editor del calendario de Comunidad (borrador): formaciones y cafecitos que se cargan mes a
 * mes. La pantalla real se entera recién al tocar "Publicar cambios", igual que las novedades.
 */
export const CalendarEventsEditor = () => {
  const [events, setEvents] = useState<ComunidadCalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  // "Ya cargados" arranca cerrado: con varios meses cargados, la lista es larga.
  const [loadedOpen, setLoadedOpen] = useState(false);
  const [newForm, setNewForm] = useState<EventForm>(EMPTY_FORM);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingForm, setEditingForm] = useState<EventForm>(EMPTY_FORM);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [docentesByKey, setDocentesByKey] = useState<Record<string, FormacionDocente[]>>({});

  const reload = async () => {
    setLoading(true);
    try {
      const evs = await comunidadAdapter.listDraftCalendar();
      setEvents(evs);
      const keys = Array.from(new Set(evs.map((e) => e.formacionKey).filter((k): k is string => !!k)));
      const lists = await Promise.all(keys.map((k) => comunidadAdapter.listDocentes(k).catch(() => [])));
      setDocentesByKey(Object.fromEntries(keys.map((k, i) => [k, lists[i]])));
    } catch {
      toastUtils.error("No pudimos cargar el calendario. Probá de nuevo en un rato.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  // Formaciones del borrador (una por key), para sumarles otra edición.
  const formaciones = Array.from(
    new Map(
      events
        .filter((e) => e.type === "formacion" && e.formacionKey)
        .map((e) => [e.formacionKey as string, { key: e.formacionKey as string, title: e.title }]),
    ).values(),
  );

  const errorMessage = (e: unknown) =>
    e instanceof Error && e.message ? e.message : "No se pudo guardar. Revisá los datos y probá de nuevo.";

  /** Guarda quién dicta la formación del evento recién guardado (si cambió). */
  const saveDocentes = async (ev: ComunidadCalendarEvent, docentes: FormacionDocente[]) => {
    if (ev.type !== "formacion" || !ev.formacionKey) return;
    const before = (docentesByKey[ev.formacionKey] ?? []).map((d) => d.psychologistId).sort().join(",");
    const after = docentes.map((d) => d.psychologistId).sort().join(",");
    if (before === after) return;
    try {
      await comunidadAdapter.setDocentes(
        ev.formacionKey,
        docentes.map((d) => d.psychologistId),
      );
    } catch {
      toastUtils.error("La formación se guardó, pero no quién la dicta. Editala y probá de nuevo.");
    }
  };

  const handleAdd = async () => {
    const input = toInput(newForm);
    if (typeof input === "string") {
      toastUtils.error(input);
      return;
    }
    setSaving(true);
    try {
      const created = await comunidadAdapter.createCalendarEvent(input);
      await saveDocentes(created, newForm.docentes);
      setNewForm(EMPTY_FORM);
      reload();
    } catch (e) {
      toastUtils.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async () => {
    if (!editingId) return;
    const input = toInput(editingForm);
    if (typeof input === "string") {
      toastUtils.error(input);
      return;
    }
    setSaving(true);
    try {
      const updated = await comunidadAdapter.updateCalendarEvent(editingId, input);
      await saveDocentes(updated, editingForm.docentes);
      setEditingId(null);
      reload();
    } catch (e) {
      toastUtils.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("¿Borrar este evento del borrador?")) return;
    try {
      await comunidadAdapter.deleteCalendarEvent(id);
      reload();
    } catch (e) {
      toastUtils.error(e instanceof Error && e.message ? e.message : "No se pudo borrar. Probá de nuevo.");
    }
  };

  const toggle = (id: string) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className="flex flex-col gap-3 rounded-[20px] bg-white p-6 shadow-[0_6px_20px_rgba(109,87,188,0.08)]">
      <h3 className="m-0 text-base font-bold text-[#2D2049]">Calendario</h3>

      <div className="flex flex-col gap-2 rounded-xl border border-dashed border-[#C9BCE8] bg-[#FAF8FF] p-3">
        <span className="text-xs font-bold uppercase tracking-wide text-[#8475B3]">Nuevo evento</span>
        <EventFields
          form={newForm}
          onChange={setNewForm}
          formaciones={formaciones}
          docentesByKey={docentesByKey}
          idPrefix="new-event"
        />
        <Button size="sm" className="self-start" onClick={handleAdd} disabled={saving}>
          <Plus size={15} className="mr-1" />
          Agregar
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-6"><CircularProgress /></div>
      ) : events.length === 0 ? (
        <p className="m-0 text-sm text-gray-400">Todavía no hay eventos cargados.</p>
      ) : (
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => setLoadedOpen((o) => !o)}
            aria-expanded={loadedOpen}
            className="flex items-center gap-1.5 self-start pt-1 text-xs font-bold uppercase tracking-wide text-[#8475B3]"
          >
            Ya cargados ({events.length})
            <ChevronDown size={14} className={`transition-transform ${loadedOpen ? "rotate-180" : ""}`} />
          </button>
          {loadedOpen && events.map((ev) => {
            const isEditing = editingId === ev.id;
            const isExpanded = isEditing || expandedIds.has(ev.id);
            return (
              <div key={ev.id} className="flex flex-col gap-2 rounded-xl border border-[#E3D9FF] px-3 py-2.5">
                {!isEditing && (
                  <button type="button" onClick={() => toggle(ev.id)} className="flex w-full items-center gap-2 text-left">
                    <span
                      className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                        ev.type === "formacion" ? "bg-[#EDE6FA] text-[#665090]" : "bg-[#FDE7EE] text-[#B4456B]"
                      }`}
                    >
                      {ev.type === "formacion" ? `Formación${ev.edition ? ` · ${ev.edition}ª ed.` : ""}` : "Cafecito"}
                    </span>
                    <span className="min-w-0 flex-grow text-sm font-semibold text-[#2D2049]">{ev.title}</span>
                    <span className="hidden shrink-0 text-[11px] text-gray-500 sm:inline">{describeWhen(ev)}</span>
                    <ChevronDown
                      size={16}
                      className={`shrink-0 text-[#665090] transition-transform ${isExpanded ? "rotate-180" : ""}`}
                    />
                  </button>
                )}
                {isExpanded &&
                  (isEditing ? (
                    <EventFields
                      form={editingForm}
                      onChange={setEditingForm}
                      formaciones={formaciones}
                      docentesByKey={docentesByKey}
                      idPrefix={`edit-${ev.id}`}
                    />
                  ) : (
                    <div className="flex flex-col gap-0.5 text-xs text-gray-500">
                      <span>{describeWhen(ev)}</span>
                      {ev.description && <span className="whitespace-pre-line">{ev.description}</span>}
                      {ev.meetUrl && <span className="break-all">{ev.meetUrl}</span>}
                      {ev.formacionKey && (
                        <span>
                          Dicta:{" "}
                          {(docentesByKey[ev.formacionKey] ?? []).map(fullName).join(", ") || "sin cargar"}
                        </span>
                      )}
                    </div>
                  ))}
                {isExpanded && (
                  <div className="flex items-center gap-2 self-end">
                    {isEditing ? (
                      <>
                        <Button size="sm" variant="outline" onClick={() => setEditingId(null)} disabled={saving}>
                          Cancelar
                        </Button>
                        <Button size="sm" onClick={saveEdit} disabled={saving}>Guardar</Button>
                      </>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-[#C9BCE8] text-[#665090]"
                        onClick={() => {
                          setEditingId(ev.id);
                          setEditingForm(toForm(ev, ev.formacionKey ? (docentesByKey[ev.formacionKey] ?? []) : []));
                        }}
                      >
                        Editar
                      </Button>
                    )}
                    <button type="button" className="text-red-400 hover:text-red-600" onClick={() => handleDelete(ev.id)}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CalendarEventsEditor;
